import { requireService } from '@profgeosoft/di-ez';
import { useTranslation } from 'react-i18next';

import { CheckboxComponent } from 'src/components/controls/checkbox-component';
import { ComboboxComponent } from 'src/components/controls/combobox-component';
import { DateOnlyPickerComponent } from 'src/components/controls/date-only-picker-component';
import { LabelComponent } from 'src/components/controls/label-component';
import { MulticomboboxComponent } from 'src/components/controls/multicombobox-component';
import { NumberInput } from 'src/components/controls/number-input';
import { StringInput } from 'src/components/controls/string-input';
import { YearOnlyPickerComponent } from 'src/components/controls/year-only-picker-component';
import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';

import type { TOption } from 'src/packages/types';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TControlView, TFieldRulesData } from 'src/services/directory-service/types';

import styles from './form-control.module.scss';

type Props = {
  controlView: TControlView;
  data: Record<string, unknown>;
  optionsService: OptionsService;
  required: boolean;
  withLabel?: boolean;
  error?: string;
  fieldRulesData?: TFieldRulesData;
  onChange(fieldId: string, value: unknown): void;
  onBlur?(fieldId: string): void;
};

export const FormControl = wrap<Props>(function FormControl({
  controlView,
  data,
  optionsService,
  withLabel = true,
  error,
  fieldRulesData,
  required,
  onChange,
  onBlur,
}) {
  const { t } = useTranslation();

  const directoryPage = requireService('directoryPage');
  const fieldTitle = t(`labels:${controlView.fieldId}.label`);

  const loadOptionsByRefQuery = async (signal?: AbortSignal): Promise<TOption[]> => {
    if (!('refQuery' in controlView)) {
      return [];
    }

    if (!controlView || !controlView.refQuery) {
      return [];
    }

    const options = await optionsService.getOptionsByRefQuery(controlView, data, signal);

    return options;
  };

  const getControlComponent = () => {
    if (controlView.control === 'Field') {
      if (controlView.type === 'String') {
        const value: string | null = (() => {
          const _value = data[controlView.fieldId];

          if (!hasValue(_value)) {
            return null;
          }

          if (typeof _value !== 'string') {
            console.error('wrong value type');
            return null;
          }

          return _value;
        })();

        return (
          <StringInput
            fieldView={controlView}
            value={value}
            error={error}
            fieldRulesData={fieldRulesData}
            onChange={onChange}
            onBlur={onBlur}
          />
        );
      }

      const value: number | null = (() => {
        const _value = data[controlView.fieldId];

        if (!hasValue(_value)) {
          return null;
        }

        if (typeof _value !== 'number') {
          console.error('wrong value type');
          return null;
        }

        return _value;
      })();

      return (
        <NumberInput
          fieldView={controlView}
          value={value}
          error={error}
          fieldRulesData={fieldRulesData}
          onChange={onChange}
          onBlur={onBlur}
        />
      );
    }

    if (controlView.control === 'ComboBox') {
      const value: string | number | null = (() => {
        const _value = data[controlView.fieldId];

        if (!hasValue(_value)) {
          return null;
        }

        if (typeof _value !== 'number' && typeof _value !== 'string') {
          console.error('wrong value type');
          return null;
        }

        return _value;
      })();

      return (
        <ComboboxComponent
          optionsService={optionsService}
          controlView={controlView}
          value={value}
          error={error}
          fieldRulesData={fieldRulesData}
          loadOptionsFn={loadOptionsByRefQuery}
          onCreateNewRecord={() => directoryPage.createNewRelatedRecord(controlView.refObjectType)}
          onChange={onChange}
          onBlur={onBlur}
        />
      );
    }

    if (controlView.control === 'MultiComboBox') {
      const value: (number | string)[] = (() => {
        const _value = data[controlView.fieldId];

        if (!hasValue(_value) || !Array.isArray(_value)) {
          return [];
        }

        if (_value.some((v) => typeof v !== 'number' && typeof v !== 'string')) {
          console.error('wrong value type', _value);
          return [];
        }

        return _value as (number | string)[];
      })();

      return (
        <MulticomboboxComponent
          optionsService={optionsService}
          controlView={controlView}
          value={value}
          error={error}
          fieldRulesData={fieldRulesData}
          loadOptionsFn={loadOptionsByRefQuery}
          onCreateNewRecord={() => directoryPage.createNewRelatedRecord(controlView.refObjectType)}
          onChange={onChange}
          onBlur={onBlur}
        />
      );
    }

    if (controlView.control === 'YearOnlyPicker') {
      const value: number | null = (() => {
        const _value = data[controlView.fieldId];

        if (!hasValue(_value)) {
          return null;
        }

        if (typeof _value !== 'number') {
          console.error('wrong value type');
          return null;
        }

        return _value;
      })();

      return (
        <YearOnlyPickerComponent
          fieldView={controlView}
          error={error}
          fieldRulesData={fieldRulesData}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
        />
      );
    }

    if (controlView.control === 'DateOnlyPicker') {
      const value: number | null = (() => {
        const _value = data[controlView.fieldId];

        if (!hasValue(_value)) {
          return null;
        }

        if (typeof _value !== 'number') {
          console.error('wrong value type');
          return null;
        }

        return _value;
      })();

      return (
        <DateOnlyPickerComponent
          fieldView={controlView}
          error={error}
          fieldRulesData={fieldRulesData}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
        />
      );
    }

    if (controlView.control === 'CheckBox') {
      const value: boolean = (() => {
        const _value = data[controlView.fieldId];

        if (typeof _value !== 'boolean') {
          console.error('wrong value type');
          return false;
        }

        return !!_value;
      })();

      return (
        <CheckboxComponent fieldView={controlView} fieldRulesData={fieldRulesData} value={value} onChange={onChange} />
      );
    }

    if (controlView.control === 'Label') {
      const value: string | number | null = (() => {
        const _value = data[controlView.fieldId];

        if (!hasValue(_value)) {
          return null;
        }

        if (typeof _value !== 'number' && typeof _value !== 'string') {
          console.error('wrong value type');
          return null;
        }

        return _value;
      })();

      return (
        <LabelComponent
          isSimpleLabel={!('refObjectType' in controlView)}
          fieldView={controlView}
          value={value}
          optionsService={optionsService}
        />
      );
    }

    return null;
  };

  if (!withLabel) {
    return getControlComponent();
  }

  const requirednessSymbol = required ? '*' : '';

  return (
    <div className={styles.wrapper}>
      <p className={styles.label}>{`${fieldTitle}${requirednessSymbol}`}</p>
      {getControlComponent()}
    </div>
  );
});
